import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { type ProposalDto, type ProposalPaymentPlan } from '@shared';
import { apiGet, apiPost, ApiError } from '../../lib/api';
import { ProposalStatusChip } from '../../components/ProposalStatusChip';
import { useLegal } from '../../lib/legal-context';

// Client-side proposal screen. Three modes driven by status:
//   - sent      : render scope + commercials + acceptance card (with disclaimer)
//   - accepted  : read-only "accepted on …" banner
//   - declined  : read-only "declined on …" + reason
//   - withdrawn : read-only "no longer available"
//   - expired   : read-only "expired"
//
// Drafts are filtered server-side; this page should never receive one.

const PLAN_LABEL: Record<ProposalPaymentPlan, string> = {
  '50_50': '50% on engagement · 50% on delivery',
  '30_40_30': '30% on engagement · 40% on milestone · 30% on delivery',
  milestones: 'Milestone-based (see below)',
  success_fee: 'Success-fee terms (see below)',
};

function formatMoney(amount_pence: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 0 })
      .format(amount_pence / 100);
  } catch {
    return `${currency} ${(amount_pence / 100).toLocaleString()}`;
  }
}

export function PortalProposalDetail() {
  const { id } = useParams<{ id: string }>();
  const legal = useLegal();
  const [proposal, setProposal] = useState<ProposalDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<null | 'accept' | 'decline'>(null);
  const [ack, setAck] = useState(false);
  const [declineMode, setDeclineMode] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  useEffect(() => {
    if (!id) return;
    apiGet<{ proposal: ProposalDto }>(`/api/proposals/${id}`)
      .then((r) => setProposal(r.proposal))
      .catch((err) => setError(err instanceof ApiError ? err.serverMessage : 'Could not load proposal.'));
  }, [id]);

  if (error) {
    return (
      <>
        <h1 className="portal-h1">Proposal not found</h1>
        <p className="portal-sub">{error}</p>
        <Link className="btn ghost" to="/portal/proposals" style={{ marginTop: 24 }}>← All proposals</Link>
      </>
    );
  }
  if (!proposal) return <p className="portal-sub">Loading…</p>;

  const isAwaitingDecision = proposal.status === 'sent';

  async function handleAccept() {
    if (!proposal || busy) return;
    if (!ack) {
      setError('Please confirm you have read the disclaimers before accepting.');
      return;
    }
    setBusy('accept');
    setError(null);
    try {
      const r = await apiPost<{ proposal: ProposalDto }>(`/api/proposals/${proposal.id}/accept`, {
        acknowledged_disclaimers: true,
      });
      setProposal(r.proposal);
    } catch (err) {
      setError(err instanceof ApiError ? err.serverMessage : 'Could not accept proposal.');
    } finally {
      setBusy(null);
    }
  }

  async function handleDecline() {
    if (!proposal || busy) return;
    setBusy('decline');
    setError(null);
    try {
      const r = await apiPost<{ proposal: ProposalDto }>(`/api/proposals/${proposal.id}/decline`, {
        reason: declineReason.trim() || undefined,
      });
      setProposal(r.proposal);
      setDeclineMode(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.serverMessage : 'Could not decline proposal.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div style={{ fontSize: 13, color: 'var(--fg-mute)', marginBottom: 8 }}>
        <Link to="/portal/proposals" style={{ borderBottom: '1px solid var(--rule)' }}>Proposals</Link>
        {' / '}<span className="rid">{proposal.id}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 className="portal-h1">Proposal {proposal.id}</h1>
          <p className="portal-sub">
            Engagement proposal from Omega · brief{' '}
            <Link to={`/portal/requests/${proposal.request_id}`} className="rid" style={{ borderBottom: '1px solid var(--rule)' }}>
              {proposal.request_id}
            </Link>
            {proposal.sent_at && <> · sent {proposal.sent_at.slice(0, 10)}</>}
          </p>
        </div>
        <ProposalStatusChip status={proposal.status} />
      </div>

      {error && (
        <div className="card" style={{ marginTop: 16, borderColor: 'var(--red)', color: 'var(--red)' }}>
          {error}
        </div>
      )}

      <div className="proposal-form">
        <div className="card">
          <h3 className="proposal-section-h">Scope</h3>
          <p className="proposal-prose">{proposal.scope_markdown}</p>
          {proposal.deliverables.length > 0 && (
            <>
              <h4 className="proposal-sub-h">Deliverables</h4>
              <ul className="proposal-list">
                {proposal.deliverables.map((d, i) => (
                  <li key={i}>
                    <strong>{d.label}</strong>
                    {d.description && <span style={{ color: 'var(--fg-soft)' }}> — {d.description}</span>}
                  </li>
                ))}
              </ul>
            </>
          )}
          {proposal.assumptions && (
            <>
              <h4 className="proposal-sub-h">Assumptions</h4>
              <p className="proposal-prose">{proposal.assumptions}</p>
            </>
          )}
        </div>

        <div className="card">
          <h3 className="proposal-section-h">Commercials</h3>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 30, letterSpacing: '-0.02em', marginBottom: 6, fontVariantNumeric: 'tabular-nums' }}>
            {formatMoney(proposal.fee_pence, proposal.currency)}
          </div>
          <div style={{ color: 'var(--fg-soft)', fontSize: 13.5, marginBottom: 18 }}>
            {PLAN_LABEL[proposal.payment_plan]}
          </div>
          <div className="proposal-kv">
            <span>Valid until</span>
            <strong>{proposal.valid_until.slice(0, 10)}</strong>
          </div>
          {proposal.sent_at && (
            <div className="proposal-kv">
              <span>Sent</span>
              <strong>{proposal.sent_at.slice(0, 10)}</strong>
            </div>
          )}
          {proposal.accepted_at && (
            <div className="proposal-kv">
              <span>Accepted</span>
              <strong>{proposal.accepted_at.slice(0, 10)}</strong>
            </div>
          )}

          {proposal.payment_plan === 'milestones' && proposal.milestones.length > 0 && (
            <>
              <h4 className="proposal-sub-h">Milestones</h4>
              <ul className="proposal-list">
                {proposal.milestones.map((m, i) => (
                  <li key={i}>
                    <strong>{m.label}</strong>
                    <span style={{ color: 'var(--fg-soft)' }}> — {formatMoney(m.amount_pence, proposal.currency)}</span>
                    {m.due_at && <span style={{ color: 'var(--fg-mute)' }}> · due {m.due_at.slice(0, 10)}</span>}
                  </li>
                ))}
              </ul>
            </>
          )}
          {proposal.payment_plan === 'success_fee' && proposal.success_fee_terms && (
            <>
              <h4 className="proposal-sub-h">Success-fee terms</h4>
              <p className="proposal-prose">{proposal.success_fee_terms}</p>
            </>
          )}
        </div>
      </div>

      {/* ── Status-specific banners ───────────────────────────────────── */}
      {isAwaitingDecision && (
        <>
          {!declineMode && (
            <div className="proposal-accept-card">
              <h3>Ready to decide?</h3>
              <p style={{ color: 'var(--fg-soft)', fontSize: 14, lineHeight: 1.55, margin: '4px 0 0' }}>
                Accepting this proposal advances the engagement to delivery and locks the scope and fee.
                Decline if anything in scope or commercials needs to change — we can issue a revised proposal.
              </p>
              <label className="proposal-ack">
                <input
                  type="checkbox"
                  checked={ack}
                  disabled={busy !== null}
                  onChange={(e) => setAck(e.target.checked)}
                />
                <span>
                  I confirm I have read and agree to the{' '}
                  <Link to="/terms" style={{ borderBottom: '1px solid currentColor' }}>Terms</Link>,{' '}
                  <Link to="/regulatory" style={{ borderBottom: '1px solid currentColor' }}>regulatory perimeter</Link>{' '}
                  and the disclaimers
                  {legal.version && <> (v{legal.version})</>}
                  {' '}associated with this engagement.
                </span>
              </label>
              <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
                <button type="button" className="btn" disabled={!ack || busy !== null} onClick={handleAccept}>
                  {busy === 'accept' ? 'Accepting…' : 'Accept proposal'}
                </button>
                <button type="button" className="btn ghost" disabled={busy !== null} onClick={() => setDeclineMode(true)}>
                  Decline
                </button>
              </div>
            </div>
          )}

          {declineMode && (
            <div className="proposal-accept-card" style={{ borderColor: 'var(--rule-strong)', background: 'var(--bg-soft)' }}>
              <h3>Decline this proposal?</h3>
              <p style={{ color: 'var(--fg-soft)', fontSize: 14, lineHeight: 1.55, margin: '4px 0 12px' }}>
                Telling us why helps Omega calibrate scope, fee, or timing on the next iteration.
              </p>
              <textarea
                className="prop-textarea"
                rows={4}
                placeholder="Optional — anything you'd like the engagement lead to know."
                value={declineReason}
                disabled={busy !== null}
                onChange={(e) => setDeclineReason(e.target.value)}
              />
              <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                <button type="button" className="btn" disabled={busy !== null} onClick={handleDecline}>
                  {busy === 'decline' ? 'Submitting…' : 'Submit decline'}
                </button>
                <button type="button" className="btn ghost" disabled={busy !== null} onClick={() => setDeclineMode(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {proposal.status === 'accepted' && (
        <div className="proposal-accept-card">
          <h3>Accepted</h3>
          <p style={{ color: 'var(--fg-soft)', fontSize: 14, lineHeight: 1.55, margin: '4px 0 0' }}>
            Your engagement is live. Watch this brief for milestone, invoice and document updates.{' '}
            <Link to={`/portal/requests/${proposal.request_id}`} style={{ borderBottom: '1px solid currentColor' }}>
              Open brief {proposal.request_id} →
            </Link>
          </p>
        </div>
      )}

      {proposal.status === 'declined' && (
        <div className="proposal-accept-card" style={{ borderColor: 'var(--rule-strong)', background: 'var(--bg-soft)' }}>
          <h3>Declined</h3>
          <p style={{ color: 'var(--fg-soft)', fontSize: 14, lineHeight: 1.55, margin: '4px 0 0' }}>
            We've recorded your decline{proposal.decline_reason && ` with the note: "${proposal.decline_reason}"`}.
            Your engagement lead will reach out to revise scope or pricing where applicable.
          </p>
        </div>
      )}

      {proposal.status === 'withdrawn' && (
        <div className="proposal-accept-card" style={{ borderColor: 'var(--rule-strong)', background: 'var(--bg-soft)' }}>
          <h3>Withdrawn by Omega</h3>
          <p style={{ color: 'var(--fg-soft)', fontSize: 14, lineHeight: 1.55, margin: '4px 0 0' }}>
            This proposal is no longer available. Your engagement lead may issue a revised one — check back here.
          </p>
        </div>
      )}

      {proposal.status === 'expired' && (
        <div className="proposal-accept-card" style={{ borderColor: 'var(--rule-strong)', background: 'var(--bg-soft)' }}>
          <h3>Expired</h3>
          <p style={{ color: 'var(--fg-soft)', fontSize: 14, lineHeight: 1.55, margin: '4px 0 0' }}>
            The valid-until date passed before a decision was recorded. Get in touch with your engagement
            lead to issue a fresh proposal.
          </p>
        </div>
      )}

      <div className="proposal-actions">
        <Link to="/portal/proposals" className="btn ghost">← Back to proposals</Link>
      </div>
    </>
  );
}
